import { avgText, srText, toRateText } from "../lib/recordFigures";
import { LineChart, Line, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

/**
 * The Dashboard's six compact charts (#1147, D225): cumulative runs as a line
 * top-left, then runs per match, batting average, strike rate, wickets and
 * economy. Two to a row on a phone, one point per match.
 *
 * Built fresh over `GET /analytics/career`'s `trend` (one point per match:
 * `runs`, `cum_runs`, `avg_after`, `sr_after`, `wickets`, `econ_after`), the same series
 * `pages/Statistics.jsx` and `pages/CareerDashboard.jsx` draw at full width.
 * Those two keep their own copies until #879 deletes Statistics.
 *
 * No minimum-count words and no "not enough matches" state: one match is one
 * point, drawn as a dot (a line needs two, so the dot is always on). The
 * colours are navy and light blue only; gold belongs to the screen's one gold
 * element, and this screen has none (R-003).
 */
const NAVY = "#0B2E59";
const BLUE = "#4A9BE0";
const AXIS = { fontSize: 10, fill: "#64748B" };
const TT = { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 };

export const STAT_CHARTS = [
  { key: "cum-runs", title: "Cumulative runs", dataKey: "cum_runs", kind: "line", color: NAVY, caption: "a line, every match" },
  { key: "runs", title: "Runs per match", dataKey: "runs", kind: "bar", color: NAVY },
  // The three rates plot the career figure AS IT STOOD after each match (the
  // round-10 board: "after each match"), so the last point is the tile's own
  // figure. `null` while the base is zero is a gap, not a 0 (#981).
  { key: "avg", title: "Batting average", dataKey: "avg_after", kind: "line", color: BLUE, rate: true, fmt: avgText },
  { key: "sr", title: "Strike rate", dataKey: "sr_after", kind: "line", color: BLUE, rate: true, fmt: srText },
  { key: "wickets", title: "Wickets per match", dataKey: "wickets", kind: "bar", color: NAVY, integer: true },
  { key: "econ", title: "Economy", dataKey: "econ_after", kind: "line", color: BLUE, rate: true, fmt: toRateText },
];

const SIZE = { width: 150, height: 110 };

function Plot({ chart, trend }) {
  const common = { data: trend, margin: { top: 6, right: 6, bottom: 0, left: 0 } };
  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
      <XAxis dataKey="date" hide />
      <YAxis tick={AXIS} width={30} allowDecimals={!chart.integer} />
      <Tooltip contentStyle={TT} />
    </>
  );
  return (
    <ResponsiveContainer width="100%" height={SIZE.height} initialDimension={SIZE}>
      {chart.kind === "bar" ? (
        <BarChart {...common}>
          {axes}
          <Bar dataKey={chart.dataKey} fill={chart.color} radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
      ) : (
        <LineChart {...common}>
          {axes}
          <Line
            type="monotone"
            dataKey={chart.dataKey}
            stroke={chart.color}
            strokeWidth={2.5}
            dot={{ r: 3, fill: chart.color }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      )}
    </ResponsiveContainer>
  );
}

/** What a screen reader gets in place of the picture: the latest value. */
const latestOf = (chart, trend) => {
  const v = trend.length ? trend[trend.length - 1][chart.dataKey] : null;
  return v == null ? null : v;
};
// Formatted like the tile above it (one decimal, economy two); a count is a count.
const shown = (chart, v) => (v == null ? "—" : chart.fmt ? chart.fmt(v) : v);
const captionOf = (chart, trend) =>
  chart.caption || (chart.rate ? "after each match" : `${trend.length} ${trend.length === 1 ? "match" : "matches"}`);

function summaryOf(chart, trend) {
  if (!trend.length) return "";
  const v = latestOf(chart, trend);
  return v == null ? `${chart.title}: no figure yet.` : `${chart.title}: latest ${shown(chart, v)}, ${trend.length} ${trend.length === 1 ? "match" : "matches"} drawn.`;
}

export default function StatCharts({ trend = [] }) {
  return (
    <div
      data-testid="stat-charts"
      // #1431: two across on a phone and a tablet, three across from 1024 (index.css, `.nw-stat-charts`).
      className="nw-stat-charts"
      data-wide="full"
    >
      {STAT_CHARTS.map((chart) => (
        <section
          key={chart.key}
          data-testid={`stat-chart-${chart.key}`}
          data-points={trend.length}
          aria-label={chart.title}
          style={{
            minWidth: 0,
            padding: "12px 8px 8px 10px",
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-card)",
          }}
        >
          <h3 className="nw-section" style={{ margin: "0 0 6px" }}>{chart.title}</h3>
          <div aria-hidden="true">
            <Plot chart={chart} trend={trend} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6, paddingRight: 2 }}>
            <span data-testid={`chart-caption-${chart.key}`} className="nw-caption" style={{ color: "var(--nw-ink-muted)" }}>
              {captionOf(chart, trend)}
            </span>
            <span data-testid={`chart-latest-${chart.key}`} className="nw-caption" style={{ color: "var(--nw-ink)", fontWeight: 600 }}>
              {trend.length ? shown(chart, latestOf(chart, trend)) : ""}
            </span>
          </div>
          <span className="sr-only">{summaryOf(chart, trend)}</span>
        </section>
      ))}
    </div>
  );
}
